import React, { useState } from "react";
import { Button } from "./ui/button";
import { X, ArrowRight, Brain, Activity, Zap, HeartPulse, Baby, Moon, Stethoscope, Sparkles } from "lucide-react";

// ---------------------------------------------------------------------------
// SpecialistMatchQuiz — lightweight 3-question modal that helps undecided
// patients arrive at a specialist choice. Resulting deep-link:
//   /login?as=patient&priority=<urgent|standard>&tag=<tag>
// PatientDashboard already pre-fills form.symptom_tags and form.priority from
// the URL, so this smoothly skips ahead in the booking wizard.
// ---------------------------------------------------------------------------

const AREAS = [
  { tag: "brain_tumor", icon: Brain,      label: "Brain scans / tumour" },
  { tag: "stroke",      icon: HeartPulse, label: "Stroke / weakness" },
  { tag: "epilepsy",    icon: Zap,        label: "Seizures / epilepsy" },
  { tag: "spine",       icon: Activity,   label: "Back / neck / spine" },
  { tag: "headache",    icon: Moon,       label: "Headache / migraine" },
  { tag: "pediatric",   icon: Baby,       label: "Child under 16" },
  { tag: "other",       icon: Stethoscope,label: "Something else" },
];

const URGENCY = [
  { value: "urgent",   label: "Today / tomorrow", desc: "New severe symptoms, scan looks scary" },
  { value: "standard", label: "Within a week",    desc: "Follow-up or second opinion on an existing report" },
];

const HAS_SCAN = [
  { value: "yes", label: "Yes" },
  { value: "no",  label: "No, just symptoms" },
];

export default function SpecialistMatchQuiz({ open, onClose, onFinish }) {
  const [step, setStep] = useState(0);
  const [area, setArea]       = useState(null);
  const [urg,  setUrg]        = useState(null);
  const [scan, setScan]       = useState(null);

  if (!open) return null;

  const reset = () => { setStep(0); setArea(null); setUrg(null); setScan(null); };
  const close = () => { reset(); onClose(); };

  const finish = () => {
    onFinish({ tag: area, priority: urg, hasScan: scan });
    reset();
  };

  const canNext = (step === 0 && area) || (step === 1 && urg) || (step === 2 && scan);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={close}
      data-testid="specialist-quiz-modal"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 bg-gradient-to-br from-[#0B5ED7] to-[#20C997] text-white relative">
          <button onClick={close} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/20" aria-label="Close">
            <X className="w-4 h-4"/>
          </button>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-white/15 border border-white/20">
            <Sparkles className="w-3 h-3"/> Find my specialist
          </div>
          <div className="mt-2 font-heading text-xl font-semibold leading-tight">
            {step === 0 && "What area of concern?"}
            {step === 1 && "How soon do you need clarity?"}
            {step === 2 && "Do you already have a scan or report?"}
          </div>
          {/* Progress dots */}
          <div className="mt-4 flex gap-1.5">
            {[0,1,2].map((i) => (
              <div key={i} className={`h-1 rounded-full transition-all ${i <= step ? "bg-white w-8" : "bg-white/30 w-4"}`} />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[55vh] overflow-y-auto">
          {step === 0 && (
            <div className="grid grid-cols-2 gap-2">
              {AREAS.map((a) => {
                const Icon = a.icon;
                const selected = area === a.tag;
                return (
                  <button
                    key={a.tag}
                    type="button"
                    onClick={() => setArea(a.tag)}
                    className={
                      "flex flex-col items-start gap-2 p-3 rounded-xl border text-left transition " +
                      (selected
                        ? "border-[#0B5ED7] bg-[#0B5ED7]/5 ring-2 ring-[#0B5ED7]/20"
                        : "border-slate-200 bg-white hover:border-slate-300")
                    }
                    data-testid={`quiz-area-${a.tag}`}
                  >
                    <div className={"w-8 h-8 rounded-lg grid place-items-center " + (selected ? "bg-[#0B5ED7] text-white" : "bg-slate-100 text-slate-600")}>
                      <Icon className="w-4 h-4"/>
                    </div>
                    <div className="text-sm font-medium text-slate-900 leading-tight">{a.label}</div>
                  </button>
                );
              })}
            </div>
          )}
          {step === 1 && (
            <div className="space-y-2">
              {URGENCY.map((u) => {
                const selected = urg === u.value;
                return (
                  <button
                    key={u.value}
                    type="button"
                    onClick={() => setUrg(u.value)}
                    className={
                      "w-full p-4 rounded-xl border text-left transition " +
                      (selected
                        ? "border-[#0B5ED7] bg-[#0B5ED7]/5 ring-2 ring-[#0B5ED7]/20"
                        : "border-slate-200 bg-white hover:border-slate-300")
                    }
                    data-testid={`quiz-urg-${u.value}`}
                  >
                    <div className="font-heading font-semibold text-slate-900 text-sm">
                      {u.label}
                      {u.value === "urgent" && <span className="ml-2 text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full border border-amber-200">Priority</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{u.desc}</div>
                  </button>
                );
              })}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-2">
              {HAS_SCAN.map((s) => {
                const selected = scan === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setScan(s.value)}
                    className={
                      "w-full p-4 rounded-xl border text-left transition " +
                      (selected
                        ? "border-[#0B5ED7] bg-[#0B5ED7]/5 ring-2 ring-[#0B5ED7]/20"
                        : "border-slate-200 bg-white hover:border-slate-300")
                    }
                    data-testid={`quiz-scan-${s.value}`}
                  >
                    <div className="font-heading font-semibold text-slate-900 text-sm">{s.label}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Back</Button>
          ) : <span/>}
          {step < 2 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext}
              className="bg-[#0B5ED7] hover:bg-[#084BB0] text-white"
              data-testid="quiz-next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          ) : (
            <Button
              onClick={finish}
              disabled={!canNext}
              className="bg-[#0B5ED7] hover:bg-[#084BB0] text-white"
              data-testid="quiz-finish-btn"
            >
              See my specialist <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
